import { useState, useEffect } from 'react';
import { Mail, Check, AlertTriangle, ArrowLeft, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';

const WriteForUs = () => {
  const [showForm, setShowForm] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    articleContent: '',
    links: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load PayPal SDK dynamically (only on this page) and render hosted button
  useEffect(() => {
    const PAYPAL_SDK_SRC = 'https://www.paypal.com/sdk/js?client-id=BAAIKKEnYeTc_FMFsrNB_FQzGkutBrh_yLwd4IVhiIYuCQ8ykg-oNiFmsK-MFEB8J6u-yoP6XvUXknyIPo&components=hosted-buttons&enable-funding=venmo&currency=USD';

    const renderButton = () => {
      if (window.paypal && document.getElementById('paypal-container-EUAU6ZS8GZVJJ')) {
        window.paypal.HostedButtons({
          hostedButtonId: 'EUAU6ZS8GZVJJ',
        }).render('#paypal-container-EUAU6ZS8GZVJJ');
      }
    };

    if (window.paypal) {
      renderButton();
      return;
    }

    let script = document.querySelector(`script[src="${PAYPAL_SDK_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = PAYPAL_SDK_SRC;
      script.async = true;
      script.onload = renderButton;
      document.head.appendChild(script);
    } else {
      script.addEventListener('load', renderButton);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          title: formData.title,
          proposal: formData.articleContent,
          category: 'guest-article',
          submission_type: 'guest',
          payment_status: paymentComplete ? 'paid' : 'pending',
          targetUrl: formData.links,
          writingSamples: ''
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8FAF7' }}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Garden & Greenhouse</span>
            </Link>
          </div>
        </header>

        <div className="py-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-12">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#2F7D4E20' }}>
                <Check className="w-8 h-8" style={{ color: '#2F7D4E' }} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Submission Received!</h1>
              <p className="text-gray-600 mb-4">
                Thank you for your guest article submission. We will review your content and send a PayPal invoice to <strong>{formData.email}</strong> within 24-48 hours.
              </p>
              <p className="text-gray-600 mb-8">
                Once payment is confirmed, your article will be published within 24-48 hours.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/">
                  <Button className="text-white" style={{ backgroundColor: '#2F7D4E' }}>
                    Return to Homepage
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => { setSubmitted(false); setShowForm(false); setFormData({ name: '', email: '', title: '', articleContent: '', links: '' }); }}>
                  Submit Another
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8FAF7' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Garden & Greenhouse</span>
          </Link>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_eco-gardens-2/artifacts/8awup57n_GardenAndGreenhouse%20FINAL%20LOGO.png" 
              alt="Garden & Greenhouse" 
              className="h-10"
            />
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-5xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Guest Article Submissions
          </h1>
          <p className="text-xl text-gray-600 mb-4" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            Publish your gardening content on Garden & Greenhouse
          </p>
          <p className="text-gray-600">
            We accept paid guest articles aligned with gardening, plants, sustainability, and greenhouse topics.
          </p>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="pb-12">
        <div className="max-w-lg mx-auto px-4">
          <div 
            className="bg-white rounded-2xl p-8 shadow-sm"
            style={{ border: '1px solid #2F7D4E20' }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Guest Article Submission
            </h2>
            <p className="text-4xl font-bold mb-6" style={{ color: '#2F7D4E' }}>
              $50 <span className="text-lg font-normal text-gray-600">USD / article</span>
            </p>
            
            <ul className="space-y-3 mb-6">
              {[
                'Published on GardenandGreenhouse.com',
                <span key="links">Up to <strong>4 do-follow links</strong> included</span>,
                'SEO-friendly formatting',
                'Editorial review before publishing',
                <span key="turnaround"><strong>24–48 hour turnaround</strong></span>
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#2F7D4E' }} />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            {/* PayPal Button */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3 text-center">Pay securely with PayPal or Venmo:</p>
              <div id="paypal-container-EUAU6ZS8GZVJJ" style={{ minHeight: '150px', width: '100%' }}></div>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or submit for invoice</span>
              </div>
            </div>

            <Button 
              onClick={() => setShowForm(true)}
              variant="outline"
              className="w-full text-lg py-6 border-2"
              style={{ borderColor: '#2F7D4E', color: '#2F7D4E' }}
            >
              Submit Article (Pay Later via Invoice)
            </Button>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      {showForm && (
        <section className="pb-12" id="submit-form">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Submit Your Article
              </h2>
              <p className="text-gray-600 mb-6">
                Fill out the form below. We'll review your submission and send a PayPal invoice within 24-48 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Name / Company *</label>
                    <Input
                      required
                      placeholder="Your name or company"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Email (for PayPal invoice) *</label>
                    <Input
                      required
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Article Title *</label>
                  <Input
                    required
                    placeholder="Your article title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Article Content *</label>
                  <textarea
                    required
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Paste your complete article here..."
                    value={formData.articleContent}
                    onChange={(e) => setFormData({ ...formData, articleContent: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Links to Include (up to 4 do-follow)</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="List your links, one per line:&#10;https://example.com - anchor text&#10;https://example2.com - anchor text"
                    value={formData.links}
                    onChange={(e) => setFormData({ ...formData, links: e.target.value })}
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Payment:</strong> After submission, you'll receive a PayPal invoice at the email provided. 
                    Your article will be published within 24-48 hours after payment is confirmed.
                  </p>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 text-white"
                    style={{ backgroundColor: '#2F7D4E' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit for Review'}
                    {!submitting && <Send className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Add-Ons */}
      <section className="pb-12">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Optional Add-Ons</h3>
          <div className="space-y-2 text-gray-700">
            <p>Additional do-follow links: <strong>$25 USD each</strong></p>
            <p>Link insertion into existing articles: <strong>$25 USD per link</strong></p>
          </div>
        </div>
      </section>

      {/* Content Restrictions */}
      <section className="pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div 
            className="rounded-lg p-6"
            style={{ backgroundColor: '#2F7D4E10', borderLeft: '4px solid #2F7D4E' }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#2F7D4E' }} />
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Content Policy</h3>
                <ul className="space-y-1 text-gray-700 mb-4">
                  <li>• No adult content</li>
                  <li>• No gambling or betting</li>
                  <li>• No misleading, spun, or low-quality AI content</li>
                </ul>
                <p className="text-sm text-gray-600 italic">
                  We reserve the right to reject submissions that do not meet editorial or ethical standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative: Email Submission */}
      <section className="pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
            <p className="text-gray-600 mb-3">Prefer to submit via email?</p>
            <div className="flex items-center justify-center gap-3 mb-2">
              <Mail className="w-5 h-5" style={{ color: '#2F7D4E' }} />
              <a 
                href="mailto:info@nugl.com?subject=Guest Article Submission" 
                className="text-lg font-semibold hover:underline"
                style={{ color: '#2F7D4E' }}
              >
                info@nugl.com
              </a>
            </div>
            <p className="text-sm text-gray-500">Invoices are payable via PayPal</p>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Important — Please Read</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>• Turnaround time: <strong>24–48 hours</strong> after payment</li>
              <li>• We do <strong>not</strong> provide writing samples</li>
              <li>• Pricing is <strong>non-negotiable</strong></li>
              <li>• All submissions are reviewed before publication</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer Disclaimer */}
      <section className="pb-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            Submission does not guarantee publication. Content is reviewed prior to acceptance and payment.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WriteForUs;
